import React, { useEffect, useState } from "react";
import { X, ShieldCheck, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, memberHeaders } from "../lib/api";

export const MembershipCheckout = ({ open, onClose, isRenewal = false }) => {
  const [info, setInfo] = useState(null);
  const [choice, setChoice] = useState("none"); // none | tier | custom
  const [tierId, setTierId] = useState(null);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/membership-info").then((r) => setInfo(r.data)).catch(() => {});
    }
  }, [open]);

  if (!open) return null;

  if (!info) {
    return (
      <div className="fixed inset-0 z-[70] bg-ink/50 flex items-center justify-center p-4" data-testid="membership-checkout">
        <div className="bg-paper rounded-lg p-10 flex items-center gap-3">
          <Loader2 size={20} className="text-trailride animate-spin" />
          <span className="font-sans text-ink/60">Loading membership…</span>
        </div>
      </div>
    );
  }

  const dues = info?.dues_amount ?? 0;
  const tiers = info?.donation_tiers || [];
  const donation =
    choice === "tier"
      ? (tiers.find((t) => t.id === tierId)?.amount || 0)
      : choice === "custom"
      ? Math.max(0, parseFloat(custom) || 0)
      : 0;
  const total = dues + donation;

  const proceed = async () => {
    setBusy(true);
    try {
      const res = await api.post(
        "/member/checkout",
        { origin_url: window.location.origin, donation_amount: donation },
        { headers: memberHeaders() }
      );
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-ink/50 flex items-start md:items-center justify-center overflow-y-auto p-4" data-testid="membership-checkout">
      <div className="bg-paper w-full max-w-lg rounded-lg my-8 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10">
          <h3 className="font-serif text-xl font-bold text-ink">{isRenewal ? "Renew membership" : "Annual membership"}</h3>
          <button onClick={onClose} aria-label="Close" data-testid="checkout-close" className="text-ink/50 hover:text-ink"><X size={20} /></button>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between border border-ink/15 rounded-md p-4 mb-6">
            <div>
              <p className="font-sans font-semibold text-ink">Annual Membership Dues</p>
              {info?.period_label && <p className="font-sans text-sm text-ink/55">{info.period_label}</p>}
            </div>
            <span className="font-serif text-2xl font-bold text-trailride" data-testid="checkout-dues">${dues.toFixed(2)}</span>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <Heart size={16} className="text-trailride" />
            <p className="font-serif text-lg font-bold text-ink">Would you like to add a donation to support Tiger Trailride's work?</p>
          </div>
          <p className="font-sans text-sm text-ink/55 mb-4">Optional — continue with dues only, or add a gift.</p>

          <div className="space-y-2 mb-4">
            {tiers.map((t) => (
              <button
                key={t.id}
                onClick={() => { setChoice("tier"); setTierId(t.id); }}
                data-testid={`donation-tier-${t.id}`}
                className={`w-full text-left flex items-start gap-3 border rounded-md p-3 transition-colors ${
                  choice === "tier" && tierId === t.id ? "border-trailride bg-trailride/[0.04]" : "border-ink/15 hover:border-ink/40"
                }`}
              >
                <span className="font-serif font-bold text-trailride text-lg w-14 shrink-0">${t.amount.toFixed(0)}</span>
                <span>
                  <span className="font-sans font-semibold text-ink block">{t.title}</span>
                  <span className="font-sans text-sm text-ink/60">{t.description}</span>
                </span>
              </button>
            ))}

            <button
              onClick={() => setChoice("custom")}
              data-testid="donation-custom-toggle"
              className={`w-full text-left flex items-center gap-3 border rounded-md p-3 transition-colors ${
                choice === "custom" ? "border-trailride bg-trailride/[0.04]" : "border-ink/15 hover:border-ink/40"
              }`}
            >
              <span className="font-sans font-semibold text-ink">Custom amount</span>
              {choice === "custom" && (
                <span className="ml-auto flex items-center gap-1">
                  <span className="text-ink/60">$</span>
                  <input
                    type="number" min="0" step="1" autoFocus
                    value={custom}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setCustom(e.target.value)}
                    data-testid="donation-custom-input"
                    className="w-24 bg-transparent border-b border-ink/30 py-1 text-ink focus:outline-none focus:border-trailride text-right"
                    placeholder="0"
                  />
                </span>
              )}
            </button>

            <button
              onClick={() => { setChoice("none"); setTierId(null); setCustom(""); }}
              data-testid="donation-none"
              className={`w-full text-left border rounded-md p-3 font-sans text-ink/70 transition-colors ${
                choice === "none" ? "border-trailride bg-trailride/[0.04] text-ink" : "border-ink/15 hover:border-ink/40"
              }`}
            >
              Continue with dues only
            </button>
          </div>

          <div className="flex items-center justify-between border-t border-ink/10 pt-4 mb-5">
            <span className="font-sans text-ink/60">Total today</span>
            <span className="font-serif text-2xl font-bold text-ink" data-testid="checkout-total">${total.toFixed(2)}</span>
          </div>

          <button
            onClick={proceed}
            disabled={busy || dues <= 0}
            data-testid="checkout-proceed"
            className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-trailride text-white font-sans font-medium hover:bg-trailride-dark disabled:opacity-60 transition-colors"
          >
            <ShieldCheck size={18} /> {busy ? "Redirecting…" : "Pay securely with Stripe"}
          </button>
          <p className="font-sans text-xs text-ink/40 text-center mt-3">Secure checkout via Stripe. Test card 4242 4242 4242 4242.</p>
        </div>
      </div>
    </div>
  );
};
